import { isModernBanner } from '@/components/card';
import {
    MediaProgramWithGuides,
    MediaProgramMetadata,
} from '@/utils/media/types';
import {
    EnumFeature,
    EnumProductAnalyticsEvent,
    EventProperties,
} from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_WIDTH } from '@calm/evexia-design-tokens';
import { Check, PlayArrowRounded } from '@mui/icons-material';
import { PlayingAnimation } from '@rhg/components/animations/playing-animation';
import { CardVariant } from '@rhg/components/card/types';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { Button, Stack, Box, Theme, SxProps } from '@rhg/libs/material';
import { Restart } from '@rhg/libs/material/icons';
import { getProgramId, ProgramState } from '@rhg/utils/programs';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ViewDetailsButton } from './view-details-button';

interface BannerActionsProps {
    program?: MediaProgramWithGuides | MediaProgramMetadata;
    variant: CardVariant;
    durationText: string;
    isPlaying?: boolean;
    playHandler: () => void;
    joinAction?: () => void;
    detailsRoute?: string;
    trackProps?: EventProperties;
    updateViewedStatus?: () => void;
}

function styleButtonStack(isFullWidth: boolean): SxProps<Theme> {
    return {
        width: isFullWidth ? '100%' : undefined,
        flexWrap: 'wrap',
    };
}

const styleButtonGrow = {
    flexGrow: 1,
    textWrap: 'nowrap',
};

const stylePlayingAnimation = {
    '& svg': {
        maxHeight: DS_HEIGHT.BUTTON_XSMALL,
        maxWidth: DS_WIDTH.BUTTON_XSMALL,
    },
};

interface BannerActionStateParams {
    variant: CardVariant;
    program?: MediaProgramWithGuides | MediaProgramMetadata;
    programState: ProgramState;
    isMobile: boolean;
    hasJoinAction: boolean;
}

export function getBannerActionState({
    variant,
    program,
    programState,
    isMobile,
    hasJoinAction,
}: BannerActionStateParams) {
    const isBannerV3ViewDetailsOnly =
        variant === CardVariant.BannerV3 &&
        !program?.isClinical &&
        (program?.contentType === 'Series' ||
            program?.contentType === 'Album' ||
            program?.contentType === 'Wisdom');
    const isJoinActionDisplayed =
        !isBannerV3ViewDetailsOnly &&
        hasJoinAction &&
        (programState === ProgramState.NotJoined ||
            programState === ProgramState.Abandoned ||
            programState === ProgramState.Completed);
    const isInProgress =
        !isBannerV3ViewDetailsOnly && programState === ProgramState.InProgress;
    const showViewDetailsButton =
        !isInProgress &&
        ((variant === CardVariant.BannerV2 && !isMobile) ||
            (variant === CardVariant.BannerV3 &&
                // Clinical programs normally get the Join/Restart button
                // instead; fall back to View Details if no joinAction was
                // wired up so the card is never left with zero CTAs.
                (!program?.isClinical || !isJoinActionDisplayed)));
    const isViewDetailsOnCard =
        showViewDetailsButton || variant === CardVariant.Banner;

    return {
        isBannerV3ViewDetailsOnly,
        isJoinActionDisplayed,
        isInProgress,
        showViewDetailsButton,
        isViewDetailsOnCard,
    };
}

export const BannerActions = ({
    program,
    joinAction,
    playHandler,
    variant,
    durationText,
    detailsRoute,
    isPlaying,
    trackProps,
    updateViewedStatus,
}: BannerActionsProps) => {
    const { currentSession, programState } =
        useProvidedProgramContextWithGuidesAndWorkflow();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const {
        isBannerV3ViewDetailsOnly,
        isJoinActionDisplayed,
        isInProgress,
        showViewDetailsButton,
    } = getBannerActionState({
        variant,
        program,
        programState,
        isMobile,
        hasJoinAction: typeof joinAction !== 'undefined',
    });
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();

    const handleBannerViewDetailsClick = useCallback(() => {
        updateViewedStatus?.();
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemClicked,
            guide: currentSession,
            program,
            packTitle,
            trackProps,
            // Explicit CTA on the card banner; per analytics convention
            // these events do not carry inherited surface context (e.g.
            // home PackClass / RowIndex / PackDisplayType / RowItemIndex)
            // even when fired from a card on home.
            clearAmbient: true,
        });
    }, [
        trackPackItemEvent,
        currentSession,
        program,
        packTitle,
        trackProps,
        updateViewedStatus,
    ]);

    const isCompleted = programState === ProgramState.Completed;
    const isBannerV3Mobile = variant === CardVariant.BannerV3 && isMobile;
    const buttonSize = isBannerV3Mobile ? 'small' : 'large';
    const isSimpleLabel = variant === CardVariant.BannerV3;
    const joinButtonLabel = formatMessage(
        programMessages[
            isCompleted
                ? isSimpleLabel
                    ? 'program.simple_restart'
                    : 'program.restart_program'
                : isSimpleLabel
                ? 'program.simple_join'
                : 'program.join_program'
        ]
    );

    return (
        <Stack direction="row" gap={4} sx={styleButtonStack(isBannerV3Mobile)}>
            {isJoinActionDisplayed && (
                <Button
                    trackButton={
                        isCompleted ? 'CtaRestartProgram' : 'CtaJoinProgram'
                    }
                    trackProps={{
                        ...(program
                            ? {
                                  ProgramName: program.title,
                                  ProgramId: getProgramId(program),
                              }
                            : {}),
                        trackFeature: EnumFeature.ClinicalProgram,
                    }}
                    size={buttonSize}
                    variant="contained"
                    startIcon={isCompleted ? <Restart /> : <Check />}
                    onClick={joinAction}
                    sx={styleButtonGrow}
                    fullWidth={isBannerV3Mobile}
                >
                    {joinButtonLabel}
                </Button>
            )}
            {isInProgress && (
                <Button
                    trackButton="ProgramPlay"
                    startIcon={
                        !isJoinActionDisplayed ? (
                            isPlaying && isModernBanner(variant) ? (
                                <Box sx={stylePlayingAnimation}>
                                    <PlayingAnimation />
                                </Box>
                            ) : (
                                <PlayArrowRounded />
                            )
                        ) : undefined
                    }
                    variant={isJoinActionDisplayed ? 'outlined' : 'contained'}
                    color="primary"
                    size={buttonSize}
                    onClick={playHandler}
                    sx={styleButtonGrow}
                    fullWidth={isBannerV3Mobile}
                    aria-label={
                        isModernBanner(variant)
                            ? formatMessage(
                                  commonMessages[
                                      'common.play_session_number_title'
                                  ],
                                  {
                                      number: currentSession?.position,
                                      title: currentSession?.title,
                                  }
                              )
                            : formatMessage(
                                  commonMessages[
                                      'common.play_session_number_title_duration'
                                  ],
                                  {
                                      number: currentSession?.position,
                                      title: currentSession?.title,
                                      duration: durationText,
                                  }
                              )
                    }
                >
                    {formatMessage(
                        commonMessages['common.play_session_number'],
                        {
                            number: currentSession?.position,
                        }
                    )}
                </Button>
            )}
            {showViewDetailsButton && (
                <ViewDetailsButton
                    showEndIcon={
                        !isBannerV3ViewDetailsOnly &&
                        !isJoinActionDisplayed &&
                        !isInProgress
                    }
                    buttonVariant={
                        isBannerV3ViewDetailsOnly ? 'contained' : 'outlined'
                    }
                    size={buttonSize}
                    fullWidth={isBannerV3Mobile}
                    detailsRoute={detailsRoute}
                    messageKey={
                        isBannerV3ViewDetailsOnly
                            ? 'common.view_details'
                            : 'common.view_program'
                    }
                />
            )}
            {variant === CardVariant.Banner && (
                <ViewDetailsButton
                    showEndIcon={!isJoinActionDisplayed && !isInProgress}
                    buttonVariant={
                        isJoinActionDisplayed || isInProgress
                            ? 'outlined'
                            : 'contained'
                    }
                    size="large"
                    onClick={handleBannerViewDetailsClick}
                    detailsRoute={detailsRoute}
                    messageKey="common.view_details"
                />
            )}
        </Stack>
    );
};
